import React from 'react';
import {
  Alert,
  Box,
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Typography,
  useTheme,
} from '@mui/material';
import { SchwachstelleList } from '../../types';
import { formatSchwachstellenStatusDisplay } from '../../utils/schwachstellenStatus';
import { getStatusColor } from './auditDetailUtils';

interface ReportReviewWarningDialogProps {
  open: boolean;
  findings: SchwachstelleList[];
  saving: boolean;
  onCancel: () => void;
  onGenerateAnyway: () => void;
}

const ReportReviewWarningDialog: React.FC<ReportReviewWarningDialogProps> = ({
  open,
  findings,
  saving,
  onCancel,
  onGenerateAnyway,
}) => {
  const theme = useTheme();
  const isDark = theme.palette.mode === 'dark';

  return (
    <Dialog open={open} onClose={saving ? undefined : onCancel} maxWidth="sm" fullWidth>
      <DialogTitle sx={{ color: 'text.primary', fontWeight: 700 }}>
        Schwachstellen noch nicht geprüft
      </DialogTitle>
      <DialogContent>
        <Alert severity="warning" sx={{ mb: 2 }}>
          Nicht alle Schwachstellen dieses Audits sind geprüft. Diese sollten noch geprüft werden,
          bevor der Report generiert wird.
        </Alert>
        <Box
          component="ul"
          sx={{
            m: 0,
            pl: 2.5,
            maxHeight: 240,
            overflow: 'auto',
          }}
        >
          {findings.map((finding) => {
            const label = formatSchwachstellenStatusDisplay(finding.status?.name);
            const color = getStatusColor(finding.status?.name);
            return (
              <Box component="li" key={finding.id} sx={{ mb: 0.75 }}>
                <Typography component="span" variant="body2" sx={{ fontWeight: 600 }}>
                  {finding.titel}
                </Typography>
                <Typography
                  component="span"
                  variant="body2"
                  sx={{ color, fontWeight: 700, ml: 1 }}
                >
                  {label}
                </Typography>
              </Box>
            );
          })}
        </Box>
      </DialogContent>
      <DialogActions sx={{ px: 3, pb: 2 }}>
        <Button
          onClick={onCancel}
          disabled={saving}
          sx={{ color: 'text.secondary', '&:hover': { bgcolor: isDark ? '#2c3358' : '#eef1f5' } }}
        >
          Abbrechen
        </Button>
        <Button
          onClick={onGenerateAnyway}
          variant="contained"
          color="warning"
          disabled={saving}
        >
          {saving ? 'Generiert...' : 'Trotzdem generieren'}
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default ReportReviewWarningDialog;
